import { UserRole } from '@/app/_shared/enums'
import { type EntityReference } from '@/app/_shared/satelliteEntity/types'
import { type ValueOf } from '@/app/_shared/types'
import { type Session } from 'next-auth'
import { signOut } from 'next-auth/react'

export function toSentenceCase(text: string | null | undefined): string {
	if (!text) return ''
	const result = text
		.replace(/([A-Z])/g, ' $1')
		.toLowerCase()
		.replace(/_/g, ' ')
		.trim()
	return result[0].toUpperCase() + result.substring(1).toLowerCase()
}

export function toTitleCase(text: string | null | undefined): string {
	if (!text) return ''
	const spacedString = text.replace(/([A-Z])/g, ' $1')
	const result = spacedString
		.toLowerCase()
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
	return result
}

export function snakeCaseToSentence(text: string): string {
	return text.replace(/^[-_]*(.)/, (_, c) => c.toUpperCase()).replace(/[-_]+(.)/g, (_, c) => ` ${c.toUpperCase()}`)
}

export function camelCaseToSentence(text: string): string {
	const result = text.replace(/([A-Z])/g, ' $1')
	return result.charAt(0).toUpperCase() + result.slice(1)
}

export function toCamelCase(text: string): string {
	if (!text) return ''
	return text.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
}

export function toSnakeCase(text: string): string {
	return text
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/\s+/g, '_')
		.toLowerCase()
}

// URL-safe slug: lowercased, runs of non-alphanumerics collapsed to a single dash, edges trimmed.
// e.g. "Pricing Criteria" -> "pricing-criteria".
// An all-digit result is prefixed with "t" (e.g. "2026" -> "t2026") so a slug can never be mistaken
// for a numeric id by routes that accept either (see resolveTable in dataStore/recordService).
export function slugify(text: string): string {
	const slug = text
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
	return /^\d+$/.test(slug) ? `t${slug}` : slug
}

export function sanitizeFields<T extends Record<string, unknown>>(data: T): T {
	const newData: T = {} as T

	for (const [key, value] of Object.entries(data)) {
		if (typeof value === 'string') {
			newData[key as keyof T] = value.trim() as T[keyof T]
			continue
		}
		newData[key as keyof T] = value as T[keyof T]
	}

	return newData
}

/** The opportunity `decimalPoints` field carries `Standard (2)` or `Extended (4)`. */
export function parseDecimalPoints(decimalPoints: string | null | undefined): 2 | 4 {
	return decimalPoints?.includes('4') ? 4 : 2
}

export function booleanToYesNo(value: boolean): string {
	return value ? 'Yes' : 'No'
}

/**
 * Normalises a typed number to US notation and cuts it to `decimals` decimal places. A comma
 * becomes a dot, and every other non-digit is dropped. The value stays a string, so a
 * half-typed entry such as `1.` survives.
 * A `type='number'` input would take its decimal separator from the browser locale instead.
 */
export function toDecimalInput(value: string, decimals: number): string {
	const [whole, ...rest] = value
		.replace(',', '.')
		.replace(/[^\d.]/g, '')
		.split('.')
	return rest.length === 0 ? whole : `${whole}.${rest.join('').slice(0, decimals)}`
}

export function removeSecondsFromTime(dateString: string | undefined): string {
	if (!dateString) return ''
	const time = dateString.split(':')
	const secondsWithAmPm = `:${time.at(-1)}`
	const amPm = secondsWithAmPm?.split(' ').at(-1) as string
	return dateString.replace(secondsWithAmPm, ` ${amPm}`)
}

export async function apiClient(apiEndpoint: string, apiKey: string, requestOpts: RequestInit = {}): Promise<Response> {
	if (!apiKey) {
		void signOut()
		return Promise.reject()
	}

	return fetch(apiEndpoint, {
		...requestOpts,
		headers: { ...requestOpts.headers, Authorization: `Bearer ${apiKey}` },
	})
}
export async function fetchResource<T>(apiEndpoint: string, defaultValue: T, apiKey: string, requestOpts: RequestInit = {}): Promise<T> {
	const result = await apiClient(apiEndpoint, apiKey, requestOpts)
	return result.ok ? result.json() : defaultValue
}

export function createApi(apiKey: string) {
	return {
		apiClient: (apiEndpoint: string, requestOpts: RequestInit = {}) => apiClient(apiEndpoint, apiKey, requestOpts),
		fetchResource: <T>(apiEndpoint: string, defaultValue: T, requestOpts: RequestInit = {}) =>
			fetchResource<T>(apiEndpoint, defaultValue, apiKey, requestOpts),
	}
}

export function stringToArray(str: string): string[] {
	return str?.replace('[', '').replace(']', '').split(',') ?? []
}

export function arrayToString(arr: unknown[]): string {
	return `[${arr.toString()}]`
}

export function omit<T extends Record<string, unknown>, K extends keyof T>(obj: T, ...keys: K[]) {
	return Object.fromEntries(Object.entries(obj).filter(([key]) => !keys.includes(key as K))) as Omit<T, K>
}

export function transformObjectValuesToString(object: Record<string, unknown>): Record<string, string> {
	return Object.entries(object).reduce(
		(acc, [currKey, currVal]) => {
			acc[currKey] = String(currVal)
			return acc
		},
		{} as Record<string, string>,
	)
}

export function objectToMap<T>(obj: Record<string, T>): Map<string, T> {
	return Object.entries(obj).reduce((acc, [key, val]) => {
		acc.set(key, val)
		return acc
	}, new Map())
}

export function sortBy<T extends Record<string, unknown>>(array: T[], sortKey: keyof T): T[] {
	return array.sort((a, b) => {
		if (a[sortKey] < b[sortKey]) return -1
		if (a[sortKey] > b[sortKey]) return 1
		return 0
	})
}

export function formatBytes(value: number): string {
	const units = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
	let l = 0
	let n = value ?? 0
	while (n >= 1024 && ++l) {
		n = n / 1024
	}
	return `${n.toFixed(n < 10 && l > 0 ? 1 : 0)} ${units[l]}`
}

export const alphabeticalWithSpaceRulePattern = {
	value: /^[A-Za-z\s]+$/,
	message: 'Only alphabetical characters and spaces are allowed',
}

export function generateRandomString(length: number): string {
	const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
	const randomString = Array.from({ length }, () => characters.charAt(Math.floor(Math.random() * characters.length))).join('')
	return randomString
}

export function arrayBufferToUrl(arrayBuffer: ArrayBuffer, type: string): string {
	return URL.createObjectURL(new Blob([arrayBuffer], { type }))
}

export function binaryTotUrl(data: string, type: string): string {
	const len = data.length
	const bytes = new Uint8Array(len)
	for (let i = 0; i < len; i++) {
		bytes[i] = data.charCodeAt(i)
	}
	return arrayBufferToUrl(bytes.buffer, type)
}

export function streamToBinary(stream: NodeJS.ReadableStream): Promise<string> {
	return new Promise((resolve, reject) => {
		const chunks: Uint8Array[] = []
		stream.on('data', chunk => {
			chunks.push(chunk)
		})
		stream.on('end', () => {
			const buffer = Buffer.concat(chunks)
			resolve(buffer.toString('binary'))
		})
		stream.on('error', reject)
	})
}

export function numberToMoney(value: number): string {
	return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

export function numberWithCommas(value: number | string | null): string | null {
	if (!value) return null
	const [intPart, decPart] = value.toString().split('.')
	const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
	return decPart !== undefined ? `${formatted}.${decPart}` : formatted
}

export function getNumberOptions(count: number): string[] {
	return Array.from({ length: count }, (_, i) => String(i + 1))
}

export function chunkArray<T>(array: T[], chunkSize: number): T[][] {
	const result: T[][] = []
	for (let i = 0; i < array.length; i += chunkSize) {
		const chunk = array.slice(i, i + chunkSize)
		result.push(chunk)
	}
	return result
}

export type MoveDirection = 'up' | 'down'
export function moveItem<T>(items: T[], index: number, direction: MoveDirection): T[] | null {
	const nextIndex = direction === 'up' ? index - 1 : index + 1
	if (nextIndex < 0 || nextIndex >= items.length) return null

	const next = [...items]
	;[next[index], next[nextIndex]] = [next[nextIndex], next[index]]
	return next
}

const addZero = (num: number) => (num < 10 ? `0${num}` : num)

export const TIME_ZONE = {
	UTC: 'UTC',
	EASTERN: 'America/New_York',
} as const
export type TimeZone = ValueOf<typeof TIME_ZONE>

const MONTH_ABBREV = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

function toValidDate(date: Date | string | null | undefined): Date | null {
	if (!date) return null
	const d = date instanceof Date ? date : new Date(date)
	return isNaN(d.getTime()) ? null : d
}

/** `YYYY-MM-DD` in the viewer's zone, for `<input type="date">`. */
export function formatDateToInput(date: Date | null): string {
	if (!date) return ''
	return `${date.getFullYear()}-${addZero(date.getMonth() + 1)}-${addZero(date.getDate())}`
}

export interface ZonedDateParts {
	year: number
	month: number
	day: number
	hour: number
	minute: number
	second: number
}

/** The wall clock that `instant` shows in `timeZone`. */
export function getZonedDateParts(instant: Date, timeZone: string): ZonedDateParts {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone,
		hourCycle: 'h23',
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
	}).formatToParts(instant)
	const get = (type: string) => Number(parts.find(p => p.type === type)?.value)
	return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute'), second: get('second') }
}

/** The browser's IANA zone name, e.g. `America/New_York`. */
export function getBrowserTimeZone(): string {
	return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/** The wall clock of `d` in `timeZone`, or in the process zone when none is given. */
function displayParts(d: Date, timeZone?: string): ZonedDateParts {
	if (timeZone) return getZonedDateParts(d, timeZone)
	return {
		year: d.getFullYear(),
		month: d.getMonth() + 1,
		day: d.getDate(),
		hour: d.getHours(),
		minute: d.getMinutes(),
		second: d.getSeconds(),
	}
}

/** `DD MON YYYY` in the viewer's zone, or in `timeZone`. */
export function formatDateToDisplay(date: Date | string | null | undefined, timeZone?: string): string {
	const d = toValidDate(date)
	if (!d) return ''
	const p = displayParts(d, timeZone)
	return `${addZero(p.day)} ${MONTH_ABBREV[p.month - 1]} ${p.year}`
}

/** `DD MON YYYY, h:mm AM/PM` in the viewer's zone, or in `timeZone`. */
export function formatDateTimeToDisplay(date: Date | string | null | undefined, timeZone?: string): string {
	const d = toValidDate(date)
	if (!d) return ''
	const p = displayParts(d, timeZone)
	const period = p.hour < 12 ? 'AM' : 'PM'
	const hours12 = p.hour % 12 || 12
	return `${formatDateToDisplay(d, timeZone)}, ${hours12}:${addZero(p.minute)} ${period}`
}

export function isValidTimeZone(timeZone: string | null | undefined): timeZone is string {
	if (!timeZone) return false
	try {
		new Intl.DateTimeFormat('en-US', { timeZone })
		return true
	} catch {
		return false
	}
}

/** Minutes that `timeZone` is ahead of UTC at `instant`. */
function zoneOffsetMinutes(instant: Date, timeZone: string): number {
	const p = getZonedDateParts(instant, timeZone)
	const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
	const instantSeconds = Math.floor(instant.getTime() / 1000) * 1000
	return Math.round((wallAsUtc - instantSeconds) / 60_000)
}

/** The UTC instant for a wall clock read in `timeZone`. */
function wallClockToUtc(p: ZonedDateParts, millisecond: number, timeZone: string): Date {
	const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, millisecond)
	if (timeZone === TIME_ZONE.UTC) return new Date(wallAsUtc)
	const firstGuess = wallAsUtc - zoneOffsetMinutes(new Date(wallAsUtc), timeZone) * 60_000
	const secondGuess = wallAsUtc - zoneOffsetMinutes(new Date(firstGuess), timeZone) * 60_000
	// The two differ only for a wall clock inside a spring-forward gap; it resolves to the later instant.
	return new Date(Math.max(firstGuess, secondGuess))
}

function isCalendarDate(year: number, month: number, day: number): boolean {
	if (month < 1 || month > 12 || day < 1) return false
	return day <= new Date(Date.UTC(year, month, 0)).getUTCDate()
}

function parseOffsetMinutes(designator: string): number {
	const sign = designator.startsWith('-') ? -1 : 1
	const digits = designator.slice(1).replace(':', '')
	return sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)))
}

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.(\d+))?\s*(Z|[+-]\d{2}:?\d{2})?)?$/i
const US_DATE_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AP]M)?)?$/i

export interface ParseSourceDateOptions {
	/** The zone a value with no zone designator was written in. */
	assumeZone: TimeZone
}

/**
 * Parse a date-time string from an external source (API payload, CSV cell) into a UTC instant.
 * Accepts ISO 8601 (`2026-06-11`, `2026-06-11T14:30:00`, `2026-06-11T14:30:00.000+0000`) and the
 * US form (`6/11/2026`, `6/11/2026 14:30`, `6/11/2026 2:30 PM`). A zone in the string is honoured.
 * A value with no zone is read in `assumeZone`. A date with no time is noon UTC, so it shows the
 * same day in every zone. Returns null for blank, malformed, or impossible input.
 */
export function parseSourceDate(value: string | null | undefined, { assumeZone }: ParseSourceDateOptions): Date | null {
	const trimmed = value?.trim() ?? ''
	if (trimmed === '') return null

	let parts: ZonedDateParts
	let millisecond = 0
	let hasTime: boolean
	let zone: string | undefined

	const iso = ISO_DATE_RE.exec(trimmed)
	if (iso) {
		const [, year, month, day, hour, minute, second, fraction, designator] = iso
		hasTime = hour !== undefined
		zone = designator
		millisecond = fraction ? Number(fraction.slice(0, 3).padEnd(3, '0')) : 0
		parts = {
			year: Number(year),
			month: Number(month),
			day: Number(day),
			hour: Number(hour ?? 0),
			minute: Number(minute ?? 0),
			second: Number(second ?? 0),
		}
	} else {
		const us = US_DATE_RE.exec(trimmed)
		if (!us) return null
		const [, month, day, year, hour, minute, second, meridiem] = us
		hasTime = hour !== undefined
		let hour24 = Number(hour ?? 0)
		if (meridiem) {
			if (hour24 < 1 || hour24 > 12) return null
			hour24 = (hour24 % 12) + (meridiem.toUpperCase() === 'PM' ? 12 : 0)
		}
		parts = {
			year: Number(year),
			month: Number(month),
			day: Number(day),
			hour: hour24,
			minute: Number(minute ?? 0),
			second: Number(second ?? 0),
		}
	}

	if (!isCalendarDate(parts.year, parts.month, parts.day)) return null
	if (parts.hour > 23 || parts.minute > 59 || parts.second > 59) return null
	if (!hasTime) return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, 12))

	if (zone) {
		const offset = zone.toUpperCase() === 'Z' ? 0 : parseOffsetMinutes(zone)
		return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, millisecond) - offset * 60_000)
	}
	return wallClockToUtc(parts, millisecond, assumeZone)
}

export function getFirstDayOfTheCurrentYear(): Date {
	const date = new Date()
	date.setMonth(0)
	date.setDate(1)
	return date
}

export const formatSession = (session: Session | null) => {
	const role = session?.user.role ?? null
	const name = session?.user.name ?? ''
	const id = session?.user.id
	const image = session?.user.image ?? null
	const lastLoginAt = session?.user.lastloginAt ?? null
	const jmtAccess = session?.user.jmtAccess ?? false
	const qmtAccess = session?.user.qmtAccess ?? false

	const isAdmin = role === UserRole.ADMIN
	const isGuest = role === UserRole.GUEST
	const isEngineer = role === UserRole.USER
	const isSiteManager = role === UserRole.SITE_MANAGER
	const site = (session?.user.site ?? null) as (EntityReference & { abbreviation: string | null }) | null
	const secondarySites = (session?.user.secondarySites ?? []) as EntityReference[]

	return {
		isAdmin,
		isGuest,
		role,
		isEngineer,
		isSiteManager,
		name,
		id,
		avatarUrl: image,
		lastLoginAt: lastLoginAt ? new Date(lastLoginAt) : null,
		userSite: site,
		userSecondarySites: secondarySites,
		jmtAccess,
		qmtAccess,
	}
}

export type CRUDReturn<T = unknown> = { error: string; field?: string } | { data: T } | null | number | void
export function hasCRUDError(returnValue: CRUDReturn): returnValue is { error: string; field?: string } {
	return !!returnValue && typeof returnValue === 'object' && 'error' in returnValue && returnValue.error.length > 0
}

export function valueFormatterDataGrid(value: unknown) {
	if (typeof value === 'boolean') return booleanToYesNo(value)
	if (value instanceof Date) return formatDateToDisplay(value) || value
	if (value === 0) return value
	return value || '-'
}

export function isNullOrEmpty(value: unknown) {
	return value === undefined || value === null || value === ''
}
